// 演示目录 / Demo catalog feature (Task 10 port of loadDemos/renderDemoLists/etc.).
// 从 /api/v1/demos 拉取目录，保留 legacy 列表渲染能力，并处理选中态与 Plan 预览联动。
// Loads /api/v1/demos, keeps legacy list rendering available, and handles selection + plan-preview wiring.

import { $maybe, escapeHtml } from "@/lib/dom";
import { formatConfigLabel } from "@/lib/config-labels";
import { log } from "@/lib/logging";
import { state } from "@/state/store";
import type { DemoABEntry, DemoSingleEntry, PlanRef } from "@/lib/types";
import { demosApi as realDemosApi } from "@/api/demos";
import { renderCardListSkeleton } from "@/features/card-list";
import { clearMarkdownInput, setPlanSummary } from "@/features/plan-panel";
import { applyDefaults } from "@/features/config-form";
import { appendLog } from "@/features/log-stream";

type DemosApiShape = Pick<typeof realDemosApi, "list">;

interface LoadOptions {
  demosApi?: DemosApiShape;
}

/**
 * 拉取演示目录并渲染卡片列表 / Load the demos catalog and populate the UI.
 */
export async function loadDemos(opts: LoadOptions = {}): Promise<void> {
  const demosApi = opts.demosApi ?? realDemosApi;
  // 首屏 demos 未加载时立即画骨架，避免卡片区空白等待。
  // Paint the skeleton on first load so the workspace is never blank while
  // the demo catalog fetch is in flight.
  if (state.workbenchView === "demo"
    && state.demos.single.length === 0
    && state.demos.ab.length === 0) {
    renderCardListSkeleton("demo");
  }
  try {
    const body = await demosApi.list();
    state.demos.single = (body.single ?? []) as DemoSingleEntry[];
    state.demos.ab = (body.ab ?? []) as DemoABEntry[];
    state.demos.reports = body.reports ?? [];

    window.dispatchEvent(new CustomEvent("ripple:demos-loaded"));
    appendLog("demo catalog loaded");
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    window.dispatchEvent(new CustomEvent("ripple:demos-failed", { detail: { message } }));
    appendLog(`demo catalog failed: ${message}`);
    log.warn("demo catalog failed", { message });
  }
}

/**
 * 按模式切换当前激活的演示 / Sync the active demo selection to the current mode.
 */
export function syncActiveDemoSelection(): void {
  if (state.mode === "ab") {
    const id = state.selectedDemo.ab;
    if (id) {
      selectDemo("ab", id);
    }
    return;
  }
  const id = state.selectedDemo.single;
  if (id) {
    selectDemo("single", id);
  }
}

type SelectKind = "single" | "ab" | "ab:A" | "ab:B" | "report";

/**
 * 选中一个演示并同步 Plan 预览 / Select a demo and sync plan preview / state.
 */
export function selectDemo(kind: SelectKind, id: string): void {
  if (!id) return;

  if (kind === "single") {
    const demo = findSingleDemo(id);
    state.selectedDemo.single = id;
    const ref: PlanRef = {
      source: "demo",
      demo_kind: "single",
      demo_id: id,
      slot: "plan",
    };
    state.planA = ref;
    clearMarkdownInput("A");
    setPlanSummary(
      "A",
      demo ? demo.plan_summary : null,
      demo ? demo.title : id,
      "demo",
      demo ? demo.plan_markdown : "",
    );
    applyDefaults(demo ? demo.defaults : null);
    refreshDemoSelectionUI();
    appendLog(`selected single demo: ${id}`);
    return;
  }

  if (kind === "ab" || kind === "ab:A" || kind === "ab:B") {
    const demo = findABDemo(id);
    state.selectedDemo.ab = id;
    applyDefaults(demo ? demo.defaults : null);

    if (kind === "ab" || kind === "ab:A") {
      state.planA = {
        source: "demo",
        demo_kind: "ab",
        demo_id: id,
        slot: "plan_a",
      };
      clearMarkdownInput("A");
      setPlanSummary(
        "A",
        demo ? demo.plan_a_summary : null,
        demo ? `${demo.title} - A` : `${id} - A`,
        "demo",
        demo ? demo.plan_a_markdown : "",
      );
    }

    if (kind === "ab" || kind === "ab:B") {
      state.planB = {
        source: "demo",
        demo_kind: "ab",
        demo_id: id,
        slot: "plan_b",
      };
      clearMarkdownInput("B");
      setPlanSummary(
        "B",
        demo ? demo.plan_b_summary : null,
        demo ? `${demo.title} - B` : `${id} - B`,
        "demo",
        demo ? demo.plan_b_markdown : "",
      );
    }

    refreshDemoSelectionUI();
    appendLog(`selected A/B demo: ${id}`);
    return;
  }

  if (kind === "report") {
    state.selectedDemo.report = id;
    refreshDemoSelectionUI();
    appendLog(`selected report demo: ${id}`);
  }
}

/**
 * 渲染 Single 与 A/B 两组卡片列表 / Render both single + A/B demo card lists.
 */
export function renderDemoLists(): void {
  renderDemoCards("single", state.demos.single);
  renderDemoCards("ab", state.demos.ab);
  refreshDemoSelectionUI();
}

// 渲染单组卡片 / Render one group of demo cards.
function renderDemoCards(
  kind: "single" | "ab",
  demos: Array<DemoSingleEntry | DemoABEntry>,
): void {
  const list = $maybe(kind === "single" ? "single-demo-list" : "ab-demo-list");
  if (!list) return;
  list.replaceChildren();

  if (!demos.length) {
    const empty = document.createElement("p");
    empty.className = "demo-empty";
    empty.textContent =
      kind === "single" ? "No single-plan demos available." : "No A/B demos available.";
    list.append(empty);
    return;
  }

  for (const demo of demos) {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "demo-card";
    card.dataset.demoKind = kind;
    card.dataset.demoId = demo.id;
    card.addEventListener("click", () => selectDemo(kind, demo.id));

    const planCount = kind === "single" ? "1 Plan" : "2 Plans";
    const metaTags = renderDemoMetaTags((demo.defaults ?? {}) as Record<string, unknown>);
    card.innerHTML = `
      <span class="demo-card-kicker">${kind === "single" ? "Single Plan" : "A/B PMF"} · ${escapeHtml(planCount)}</span>
      <strong>${escapeHtml(demo.title || demo.id)}</strong>
      <span class="demo-card-desc">${escapeHtml(demo.description || demo.id)}</span>
      <span class="demo-card-meta">
        ${metaTags}
      </span>
    `;
    list.append(card);
  }
}

// 生成单个卡片下方的 meta 徽章 / Build the meta badge HTML for one card.
function renderDemoMetaTags(defaults: Record<string, unknown>): string {
  return (["channel", "vertical", "platform"] as const)
    .map((key) => {
      const raw = String(defaults[key] ?? "");
      const label = formatConfigLabel(key, raw);
      const title = raw && raw !== label ? ` title="${escapeHtml(raw)}"` : "";
      return `<span${title}>${escapeHtml(label)}</span>`;
    })
    .join("");
}

/**
 * 根据 selectedDemo 刷新卡片 active 样式 / Refresh card active class + aria-pressed.
 */
export function refreshDemoSelectionUI(): void {
  const selectedSingle = state.selectedDemo.single ?? "";
  const selectedAB = state.selectedDemo.ab ?? "";
  for (const card of Array.from(document.querySelectorAll<HTMLElement>(".demo-card"))) {
    const kind = card.dataset["demoKind"];
    const id = card.dataset["demoId"] ?? "";
    const active = kind === "single" ? id === selectedSingle : id === selectedAB;
    card.classList.toggle("active", active);
    card.setAttribute("aria-pressed", String(active));
  }
}

// 注：PlanRef -> Markdown 的查找统一在 plan-panel.getMarkdownForPlanRef，
// 避免与 demo-catalog 的静态循环依赖。
// Note: PlanRef -> Markdown lookup lives in plan-panel.getMarkdownForPlanRef
// to avoid a static cycle with demo-catalog.

// 内部 helper：按 id 查单模 demo / Lookup a single-mode demo by id.
function findSingleDemo(id: string): DemoSingleEntry | undefined {
  return state.demos.single.find((d) => d.id === id);
}

// 内部 helper：按 id 查 A/B demo / Lookup an A/B demo by id.
function findABDemo(id: string): DemoABEntry | undefined {
  return state.demos.ab.find((d) => d.id === id);
}
